// Frontend types - no MongoDB dependencies
type Decimal128 = any; // Simplified type for frontend

// Chain and Transaction Types
export interface ChainState {
  chainId: number;
  name: string;
  latestBlock: number;
  lastProcessedBlock: number;
  contractAddress: string;
  balance: string;
  isHealthy: boolean;
  lastCheckTime: number;
  rpcEndpoints: string[];
  activeRpcIndex: number;
}

export interface BridgeTransaction {
  id: string;
  srcNonce: string;
  srcChainId: string;
  destChainId: string;
  sender: string;
  receiver: string;
  amount: string;
  amountHuman: Decimal128;
  isLockMode: boolean;
  extraData?: string;
  
  // Source chain info
  requestTxHash: string;
  requestBlockNumber: number;
  requestTimestamp: number;
  
  // Target chain info
  executeTxHash?: string;
  executeBlockNumber?: number;
  executeTimestamp?: number;
  
  // Approval info
  approvals: ApprovalRecord[];
  totalApprovals: number;
  requiredApprovals: number;
  
  // Status
  status: TransactionStatus;
  riskLevel: RiskLevel;
  
  // Audit info
  lastAuditTime?: number;
  auditNotes?: string[];
  
  createdAt: string;
  updatedAt: string;
}

// Alias for compatibility
export type AuditTransaction = BridgeTransaction;

export interface ApprovalRecord {
  validator: string;
  txHash: string;
  blockNumber: number;
  timestamp: number;
  receiveHash: string;
}

export enum TransactionStatus {
  PENDING = 'pending',
  APPROVED = 'approved',
  EXECUTED = 'executed',
  FAILED = 'failed',
  TIMEOUT = 'timeout',
  DISPUTED = 'disputed'
}

export enum RiskLevel {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
  CRITICAL = 'critical'
}

// Alert Types
export interface AuditAlert {
  id: string;
  type: AlertType;
  severity: AlertSeverity;
  title: string;
  description: string;
  transactionId?: string;
  chainId?: number;
  amount?: string;
  timestamp: number;
  resolved: boolean;
  resolvedAt?: number;
  resolvedBy?: string;
  createdAt: string;
}

export enum AlertType {
  PENDING_TIMEOUT = 'pending_timeout',
  AMOUNT_MISMATCH = 'amount_mismatch',
  VALIDATOR_OFFLINE = 'validator_offline',
  DOUBLE_SPENDING = 'double_spending',
  UNUSUAL_VOLUME = 'unusual_volume',
  BALANCE_WARNING = 'balance_warning',
  EXECUTION_FAILED = 'execution_failed',
  VALIDATOR_DISPUTE = 'validator_dispute',
  CHAIN_REORG = 'chain_reorg'
}

export enum AlertSeverity {
  INFO = 'info',
  WARNING = 'warning',
  ERROR = 'error',
  CRITICAL = 'critical'
}

// Reconciliation Types
export interface ReconciliationResult {
  chainId: number;
  startBlock: number;
  endBlock: number;
  totalEvents: number;
  processedEvents: number;
  missingEvents: number;
  duplicateEvents: number;
  inconsistentEvents: number;
  balanceMatches: boolean;
  expectedBalance: string;
  actualBalance: string;
  balanceDifference: string;
  timestamp: number;
}

// Report Types
export interface AuditReport {
  id: string;
  reportType: 'hourly' | 'daily' | 'weekly' | 'monthly' | 'adhoc';
  startTime: number;
  endTime: number;
  
  // Statistics
  totalTransactions: number;
  successfulTransactions: number;
  failedTransactions: number;
  pendingTransactions: number;
  
  // Volume
  totalVolume: string;
  bscToRoninVolume: string;
  roninToBscVolume: string;
  
  // Performance
  averageProcessingTime: number;
  maxProcessingTime: number;
  validatorPerformance: ValidatorPerformance[];
  
  createdAt: string;
}

export interface ValidatorPerformance {
  validator: string;
  totalApprovals: number;
  successRate: number;
  averageResponseTime: number;
  lastActiveTime: number;
  uptime: number;
  responsiveness: number;
}

// Health Check Types
export interface HealthStatus {
  status: string;
  timestamp: string;
  database: boolean;
  chains: {
    chainId: number;
    name: string;
    healthy: boolean;
    lastCheck: string;
  }[];
}

// API Response Types
export interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  limit: number;
  pages: number;
}

// Dashboard Statistics
export interface DashboardStats {
  totalTransactions: number;
  successRate: number;
  totalVolume: string;
  activeBridges: number;
  pendingTransactions: number;
  unresolvedAlerts: number;
  averageProcessingTime: number;
  validatorCount: number;
}

// Chart Data Types
export interface ChartDataPoint {
  timestamp: number;
  value: number;
  label?: string;
}

export interface TransactionVolumeData {
  date: string;
  bscToRonin: number;
  roninToBsc: number;
  total: number;
}

export interface ValidatorData {
  validator: string;
  name?: string;
  isOnline: boolean;
  totalApprovals: number;
  successRate: number;
  lastActiveTime: number;
  responseTime: number;
}

// Validator Health Monitoring Types
export interface ValidatorHealthStatus {
  validator: string;
  name?: string;
  isHealthy: boolean;
  lastChecked: number;
  responseTime: number;
  statusCode?: number;
  errorMessage?: string;
  consecutiveFailures: number;
  services: any[];
}

export interface ValidatorHealthSummary {
  totalValidators: number;
  healthyValidators: number;
  unhealthyValidators: number;
  averageResponseTime: number;
  validatorStatuses: ValidatorHealthStatus[];
}

// Filter and Sort Types
export interface TransactionFilter {
  status?: TransactionStatus[];
  riskLevel?: RiskLevel[];
  dateRange?: {
    start: Date;
    end: Date;
  };
  minAmount?: string;
  maxAmount?: string;
  chains?: number[];
}

export interface AlertFilter {
  severity?: AlertSeverity[];
  type?: AlertType[];
  resolved?: boolean;
  dateRange?: {
    start: Date;
    end: Date;
  };
}

export type SortDirection = 'asc' | 'desc';

export interface SortConfig {
  field: string;
  direction: SortDirection;
}

// Authentication Types
export enum UserRole {
  ADMIN = 'admin',
  OPERATOR = 'operator',
  VIEWER = 'viewer'
}

export interface User {
  id: string;
  username: string;
  email: string;
  role: UserRole;
  isActive: boolean;
  lastLogin?: number;
  createdAt: string;
  updatedAt: string;
}

export interface LoginRequest {
  username: string;
  password: string;
}

export interface LoginResponse {
  success: boolean;
  token?: string;
  user?: {
    id: string;
    username: string;
    email: string;
    role: UserRole;
  };
  expiresIn?: number;
  error?: string;
}

export interface AuthState {
  isAuthenticated: boolean;
  user: User | null;
  token: string | null;
  loading: boolean;
  error: string | null;
}

export interface CreateUserRequest {
  username: string;
  email: string;
  password: string;
  role: UserRole;
}

export interface CreateUserResponse {
  success: boolean;
  userId?: string;
  error?: string;
} 